import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import Lottie from 'lottie-react';
import { 
    getAllWhatsappNumbers, 
    createWhatsappNumber, 
    deleteWhatsappNumber, 
    reassignAssistantToWhatsappNumber 
} from '../utils/api/whatsappAPI';
import { getAllAssistant } from '../utils/api/assistantAPI';
import { getWhatsAppQRCode, getWhatsAppStatus, disconnectWhatsApp, reconnectWhatsApp } from '../utils/api/whatsappAuthAPI';

const WhatsappSettingsLayer = () => {
    const userOrgId = useSelector((state) => state?.user?.user?.organizationId);
    
    const [whatsappNumbers, setWhatsappNumbers] = useState([]);
    const [assistants, setAssistants] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showReassignModal, setShowReassignModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);
    const [selectedNumber, setSelectedNumber] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [qrCodeData, setQrCodeData] = useState(null);
    const [isGeneratingQR, setIsGeneratingQR] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [isFetchingQR, setIsFetchingQR] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState(null); // 'connected', 'connecting', 'disconnected'
    const [previousStatus, setPreviousStatus] = useState(null); // Track previous status to detect changes
    const [successAnimationData, setSuccessAnimationData] = useState(null);
    const [transferAnimationData, setTransferAnimationData] = useState(null);
    const statusIntervalRef = useRef(null);

    // Load Lottie animations
    useEffect(() => {
        // Load success animation
        fetch('/assets/lottie/sucess.json')
            .then(res => res.json())
            .then(data => setSuccessAnimationData(data))
            .catch(err => console.error('Failed to load success animation:', err));
        
        // Load transfer animation
        fetch('/assets/lottie/transfer.json')
            .then(res => res.json())
            .then(data => setTransferAnimationData(data))
            .catch(err => console.error('Failed to load transfer animation:', err));
    }, []);

    // Cleanup interval when dialog closes or component unmounts
    useEffect(() => {
        return () => {
            if (statusIntervalRef.current) {
                clearInterval(statusIntervalRef.current);
                statusIntervalRef.current = null;
            }
        };
    }, []);

    // Clear interval when dialog closes
    useEffect(() => {
        if (!showQRModal && statusIntervalRef.current) {
            clearInterval(statusIntervalRef.current);
            statusIntervalRef.current = null;
        }
    }, [showQRModal]);

    // Fetch QR code when status changes to disconnected (outside of checkStatus)
    useEffect(() => {
        if (showQRModal && selectedNumber && connectionStatus === 'disconnected' && previousStatus !== 'disconnected') {
            fetchQRCode(selectedNumber.whatsappNumber);
        }
    }, [connectionStatus, showQRModal, selectedNumber, previousStatus]);
    const [addFormData, setAddFormData] = useState({
        whatsappNumber: '',
        assistantId: ''
    });
    
    const [reassignFormData, setReassignFormData] = useState({
        assistantId: ''
    });

    // Fetch WhatsApp numbers and assistants
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                if (userOrgId) {
                    const [numbersData, assistantsData] = await Promise.all([
                        getAllWhatsappNumbers(),
                        getAllAssistant(userOrgId)
                    ]);
                    
                    setWhatsappNumbers(numbersData?.whatsappNumbers || []);
                    setAssistants(assistantsData?.assistants || []);
                }
            } catch (error) {
                toast.error(error.message || 'Failed to fetch WhatsApp settings');
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchData();
    }, [userOrgId]);

    // Handle add WhatsApp number
    const handleAddNumber = async (e) => {
        e.preventDefault();
        
        if (!addFormData.whatsappNumber || !addFormData.assistantId) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await createWhatsappNumber({
                whatsappNumber: addFormData.whatsappNumber,
                assistantId: addFormData.assistantId,
                organizationId: userOrgId
            });
            
            toast.success(response.message || 'WhatsApp number added successfully');
            setShowAddModal(false);
            setAddFormData({ whatsappNumber: '', assistantId: '' });
            
            // Refresh data
            const numbersData = await getAllWhatsappNumbers(userOrgId);
            setWhatsappNumbers(numbersData?.whatsappNumbers || []);
        } catch (error) {
            toast.error(error.message || 'Failed to add WhatsApp number');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle reassign assistant
    const handleReassignAssistant = async (e) => {
        e.preventDefault();
        
        if (!reassignFormData.assistantId) {
            toast.error('Please select an assistant');
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await reassignAssistantToWhatsappNumber(
                selectedNumber.id,
                reassignFormData.assistantId
            );
            
            toast.success(response.message || 'Assistant reassigned successfully');
            setShowReassignModal(false);
            setReassignFormData({ assistantId: '' });
            setSelectedNumber(null);
            
            // Refresh data
            const numbersData = await getAllWhatsappNumbers(userOrgId);
            setWhatsappNumbers(numbersData?.whatsappNumbers || []);
        } catch (error) {
            toast.error(error.message || 'Failed to reassign assistant');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle delete WhatsApp number
     // Handle delete WhatsApp number
     const handleDeleteNumber = async () => {
        try {
            setIsSubmitting(true);
            console.log(selectedNumber);
            
            // Disconnect WhatsApp before deleting
            if (selectedNumber?.whatsappNumber) {
                try {
                    await disconnectWhatsApp(selectedNumber.whatsappNumber);
                } catch (disconnectError) {
                    console.error('Error disconnecting WhatsApp:', disconnectError);
                    // Continue with deletion even if disconnect fails
                }
            }
            
            // Delete the WhatsApp number
            const response = await deleteWhatsappNumber(selectedNumber.id);
            toast.success(response.message || 'WhatsApp number deleted successfully');
            setShowDeleteModal(false);
            setSelectedNumber(null);
            
            // Refresh data
            const numbersData = await getAllWhatsappNumbers(userOrgId);
            setWhatsappNumbers(numbersData?.whatsappNumbers || []);
        } catch (error) {
            toast.error(error.message || 'Failed to delete WhatsApp number');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Open reassign modal
    const openReassignModal = (number) => {
        setSelectedNumber(number);
        setReassignFormData({ assistantId: number.assistantId || '' });
        setShowReassignModal(true);
    };

    // Open delete modal
    const openDeleteModal = (number) => {
        setSelectedNumber(number);
        setShowDeleteModal(true);
    };

    // Fetch QR code function
    const fetchQRCode = async (whatsappNumber) => {
        setIsFetchingQR(true);
        try {
            const qrResponse = await getWhatsAppQRCode(whatsappNumber);
            const qrData = qrResponse.qrCode || qrResponse.qr || qrResponse.data || qrResponse;
            if (qrData) {
                setQrCodeData(qrData);
            } else {
                setQrCodeData(null);
            }
        } catch (qrError) {
            console.error('Failed to fetch QR code:', qrError);
            setQrCodeData(null);
        } finally {
            setIsFetchingQR(false);
        }
    };

    // Check status function - ONLY checks status, doesn't fetch QR code
    const checkStatus = async (whatsappNumber) => {
        try {
            const response = await getWhatsAppStatus(whatsappNumber);
            // Determine connection status from API response
            const status = response.status || response.connectionStatus;
            const connected = status === 'connected' || response.connected === true || response.isConnected === true;
            const connecting = status === 'connecting' || response.connecting === true || response.isConnecting === true;
            
            setIsConnected(connected);
            
            // Determine new status
            let newStatus;
            if (connected) {
                newStatus = 'connected';
                // If connected, stop the interval
                if (statusIntervalRef.current) {
                    clearInterval(statusIntervalRef.current);
                    statusIntervalRef.current = null;
                }
            } else if (connecting) {
                newStatus = 'connecting';
            } else {
                newStatus = 'disconnected';
            }
            
            // Update status
            setPreviousStatus(newStatus);
            setConnectionStatus(newStatus);
        } catch (error) {
            console.error('Failed to check status:', error);
            setIsConnected(false);
            setConnectionStatus('disconnected');
        }
    };

    // Handle generate QR code - open dialog and check status
    const handleGenerateQR = async (number) => {
        setSelectedNumber(number);
        setShowQRModal(true);
        setIsCheckingStatus(true);
        setConnectionStatus(null); // Reset status when opening dialog
        setPreviousStatus(null); // Reset previous status
        setQrCodeData(null); // Reset QR code data
        
        // Initial status check (only checks status, doesn't fetch QR)
        // QR code will be fetched by useEffect when status becomes 'disconnected'
        await checkStatus(number.whatsappNumber);
        setIsCheckingStatus(false);
        
        // Set up interval to check status every 2 seconds (only checks status, not QR)
        if (statusIntervalRef.current) {
            clearInterval(statusIntervalRef.current);
        }
        statusIntervalRef.current = setInterval(() => {
            checkStatus(number.whatsappNumber);
        }, 2000);
    };


    // Handle reconnect WhatsApp - regenerates QR code
    const handleReconnect = async () => {
        if (!selectedNumber) return;
        
        try {
            setIsReconnecting(true);
            setConnectionStatus('connecting'); // Set status to connecting immediately
            setQrCodeData(null); // Clear existing QR code
            await reconnectWhatsApp(selectedNumber.whatsappNumber);
            toast.success('QR code is regenerating. Please wait...');
            // Fetch new QR code after reconnecting
            await fetchQRCode(selectedNumber.whatsappNumber);
            // Check status after a short delay
            setTimeout(() => {
                checkStatus(selectedNumber.whatsappNumber);
            }, 100);
        } catch (error) {
            toast.error(error.message || 'Failed to reconnect WhatsApp');
            setConnectionStatus('disconnected');
        } finally {
            setIsReconnecting(false);
        }
    };

    // Handle disconnect WhatsApp
    const handleDisconnect = async () => {
        if (!selectedNumber) return;
        
        try {
            setIsDisconnecting(true);
            await disconnectWhatsApp(selectedNumber.whatsappNumber);
            setShowQRModal(false);
            setIsConnected(false);
            setConnectionStatus('disconnected');
            toast.success('WhatsApp disconnected successfully');
        } catch (error) {
            toast.error(error.message || 'Failed to disconnect WhatsApp');
        } finally {
            setIsDisconnecting(false);
        }
    };

    // Close QR modal and clear interval
    const closeQRModal = () => {
        if (statusIntervalRef.current) {
            clearInterval(statusIntervalRef.current);
            statusIntervalRef.current = null;
        }
        setConnectionStatus(null); // Reset status when closing
        setShowQRModal(false);
    };

    // Empty state - no numbers
    const renderEmptyState = () => (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="card h-100 p-0 radius-12"
        >
            <div
                style={{
                    height: '60vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '40px'
                }}
            >
                <Icon icon="mdi:whatsapp" width={64} height={64} style={{ color: '#6b7280', marginBottom: '20px' }} />
                <h6 className="fw-semibold text-primary-light mb-12">No WhatsApp Numbers</h6>
                <p className="text-secondary-light text-xs mb-24">
                    Add a WhatsApp number to start managing your WhatsApp integration.
                </p>
                <button
                    type="button"
                    className="btn btn-primary-500 text-xs btn-sm px-24 py-12 radius-8"
                    onClick={() => setShowAddModal(true)}
                >

                    Add WhatsApp Number
                </button>
            </div>
        </motion.div>
    );

    // Loading state
    if (isLoading) {
        return (
            <div className="card h-100 p-0 radius-12 overflow-hidden">
                <div className="card-body p-40 text-center">
                    <div className="spinner-border text-primary-500" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    // Show empty state if no numbers
    if (!whatsappNumbers || whatsappNumbers.length === 0) {
        return (
            <>
                {renderEmptyState()}
                {/* Add Number Modal */}
                {showAddModal && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 1050,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                        onClick={() => !isSubmitting && setShowAddModal(false)}
                    >
                        <div
                            style={{
                                backgroundColor: '#fff',
                                borderRadius: '12px',
                                padding: '32px',
                                width: '90%',
                                maxWidth: '500px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h6 className="fw-semibold text-primary-light mb-24">Add WhatsApp Number</h6>
                            <form onSubmit={handleAddNumber}>
                                <div className="mb-20">
                                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                        Phone Number <span className="text-danger-600">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        className="form-control radius-8"
                                        placeholder="Phone number with country code (Start with +)"
                                        value={addFormData.whatsappNumber}
                                        onChange={(e) => setAddFormData({ ...addFormData, whatsappNumber: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="mb-24">
                                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                        Assistant <span className="text-danger-600">*</span>
                                    </label>
                                    <div className="position-relative">
                                        <select
                                            className="form-control radius-8"
                                            value={addFormData.assistantId}
                                            onChange={(e) => setAddFormData({ ...addFormData, assistantId: e.target.value })}
                                            required
                                            style={{
                                                appearance: 'none',
                                                paddingRight: '40px'
                                            }}
                                        >
                                            <option value="">Select Assistant</option>
                                            {assistants.map((assistant) => (
                                                <option key={assistant.id} value={assistant.id}>
                                                    {assistant.name}
                                                </option>
                                            ))}
                                        </select>
                                        <Icon 
                                            icon="lucide:chevron-down" 
                                            width="18" 
                                            height="18" 
                                            style={{
                                                position: 'absolute',
                                                right: '12px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                pointerEvents: 'none',
                                                color: '#6c757d'
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="d-flex gap-12 justify-content-end">
                                    <button
                                        type="button"
                                        className="btn btn-secondary-500 text-sm btn-sm px-24 py-12 radius-8"
                                        onClick={() => setShowAddModal(false)}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary-500 text-sm btn-sm px-24 py-12 radius-8"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Adding...' : 'Add Number'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </>
        );
    }

    // Show numbers list
    return (
        <>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="card h-100 p-0 radius-12 overflow-hidden"
            >
                {/* Card Header */}
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                    <h6 className="text-lg fw-semibold mb-0">

                        WhatsApp Numbers
                    </h6>
                    <button
                        type="button"
                        className="btn btn-primary-500 text-sm btn-sm px-20 py-10 radius-8"
                        onClick={() => setShowAddModal(true)}
                        style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}
                    >
                        <Icon icon="mdi:plus" className="me-8" />
                        Add Number
                    </button>
                </div>

                {/* Card Body */}
                <div className="card-body p-24">
                    <div className="table-responsive scroll-sm">
                        <table className="table bordered-table sm-table mb-0">
                            <thead>
                                <tr>
                                    <th className="text-center">Phone Number</th>
                                    <th className="text-center">Assistant</th>
                                    <th className="text-center">Generate QR</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {whatsappNumbers.map((number) => (
                                    <tr key={number.id}>
                                        <td className="text-center">{number.whatsappNumber || '-'}</td>
                                        <td className="text-center">
                                            {number.assistant?.name || 'Not Assigned'}
                                        </td>
                                        <td className="text-center">
                                            <button
                                                type="button"
                                                onClick={() => handleGenerateQR(number)}
                                                className="btn btn-primary-500 text-sm btn-sm px-24 py-12 radius-8"
                                                title="Generate QR Code"
                                                disabled={isGeneratingQR}
                                                style={{ margin: '0 auto' }}
                                            >
                                                {isGeneratingQR ? 'Generating...' : 'Generate QR'}
                                            </button>
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex align-items-center gap-10 justify-content-center">
                                                <button
                                                    type="button"
                                                    onClick={() => openReassignModal(number)}
                                                    className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    title="Reassign Assistant"
                                                >
                                                    <Icon icon="lucide:edit" className="menu-icon" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => openDeleteModal(number)}
                                                    className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    title="Delete Number"
                                                >
                                                    <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </motion.div>

            {/* Add Number Modal */}
            {showAddModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1050,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                    onClick={() => !isSubmitting && setShowAddModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            padding: '32px',
                            width: '90%',
                            maxWidth: '500px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h6 className="fw-semibold text-primary-light mb-24">Add WhatsApp Number</h6>
                        <form onSubmit={handleAddNumber}>
                            <div className="mb-20">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                    Phone Number <span className="text-danger-600">*</span>
                                </label>
                                <input
                                    type="tel"
                                    className="form-control radius-8"
                                    placeholder="Enter WhatsApp number (+code)."
                                    value={addFormData.whatsappNumber}
                                    onChange={(e) => setAddFormData({ ...addFormData, whatsappNumber: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="mb-24">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                    Assistant <span className="text-danger-600">*</span>
                                </label>
                                <div className="position-relative">
                                    <select
                                        className="form-control radius-8"
                                        value={addFormData.assistantId}
                                        onChange={(e) => setAddFormData({ ...addFormData, assistantId: e.target.value })}
                                        required
                                        style={{
                                            appearance: 'none',
                                            paddingRight: '40px'
                                        }}
                                    >
                                        <option value="">Select Assistant</option>
                                        {assistants.map((assistant) => (
                                            <option key={assistant.id} value={assistant.id}>
                                                {assistant.name}
                                            </option>
                                        ))}
                                    </select>
                                    <Icon 
                                        icon="lucide:chevron-down" 
                                        width="18" 
                                        height="18" 
                                        style={{
                                            position: 'absolute',
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            pointerEvents: 'none',
                                            color: '#6c757d'
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="d-flex gap-12 justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-secondary-500 text-sm btn-sm px-24 py-12 radius-8"
                                    onClick={() => setShowAddModal(false)}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary-500 text-sm btn-sm px-24 py-12 radius-8"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Adding...' : 'Add Number'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Reassign Assistant Modal */}
            {showReassignModal && selectedNumber && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1050,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                    onClick={() => !isSubmitting && setShowReassignModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            padding: '32px',
                            width: '90%',
                            maxWidth: '500px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h6 className="fw-semibold text-primary-light mb-24">Reassign Assistant</h6>
                        <p className="text-secondary-light text-sm mb-24">
                            Reassign assistant for WhatsApp number: <strong>{selectedNumber.whatsappNumber}</strong>
                        </p>
                        <form onSubmit={handleReassignAssistant}>
                            <div className="mb-24">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                    Assistant <span className="text-danger-600">*</span>
                                </label>
                                <div className="position-relative">
                                    <select
                                        className="form-control radius-8"
                                        value={reassignFormData.assistantId}
                                        onChange={(e) => setReassignFormData({ assistantId: e.target.value })}
                                        required
                                        style={{
                                            appearance: 'none',
                                            paddingRight: '40px'
                                        }}
                                    >
                                        <option value="">Select Assistant</option>
                                        {assistants.map((assistant) => (
                                            <option key={assistant.id} value={assistant.id}>
                                                {assistant.name}
                                            </option>
                                        ))}
                                    </select>
                                    <Icon 
                                        icon="lucide:chevron-down" 
                                        width="18" 
                                        height="18" 
                                        style={{
                                            position: 'absolute',
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            pointerEvents: 'none',
                                            color: '#6c757d'
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="d-flex gap-12 justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-secondary-500 text-sm btn-sm px-24 py-12 radius-8"
                                    onClick={() => setShowReassignModal(false)}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary-500 text-sm btn-sm px-24 py-12 radius-8"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Reassigning...' : 'Reassign'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* QR Code Modal */}
            {showQRModal && selectedNumber && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1050,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                    onClick={closeQRModal}
                >
                    <div
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            width: '90%',
                            maxWidth: '600px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            display: 'flex',
                            flexDirection: 'column',
                            maxHeight: '90vh',
                            overflow: 'hidden'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Title Section */}
                        <div
                            style={{
                                padding: '24px 32px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <h6 className="fw-semibold text-primary-light mb-0" style={{ fontSize: '18px' }}>
                                WhatsApp Connection
                            </h6>
                            <button
                                type="button"
                                onClick={closeQRModal}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#6b7280',
                                    padding: 0,
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {/* Body Section */}
                        <div
                            style={{
                                padding: '32px',
                                flex: 1,
                                overflowY: 'auto'
                            }}
                        >
                            {isCheckingStatus ? (
                                <div className="text-center">
                                    <div className="spinner-border text-primary-500" role="status">
                                        <span className="visually-hidden">Checking status...</span>
                                    </div>
                                </div>
                            ) : connectionStatus === 'connected' ? (
                                <>
                                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                                        {successAnimationData && (
                                            <Lottie
                                                animationData={successAnimationData}
                                                style={{ width: '150px', height: '150px', margin: '0 auto' }}
                                                loop={false}
                                            />
                                        )}
                                    </div>
                                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                                        <h6 className="fw-semibold text-primary-light mb-8">WhatsApp Connected</h6>
                                        <p className="text-secondary-light text-sm">
                                            Your WhatsApp account is successfully connected and You can start using it after sucessfull connection on your device as well as in the app.
                                        </p>
                                    </div>
                                </>
                            ) : connectionStatus === 'connecting' || isFetchingQR ? (
                                <>
                                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                                        {transferAnimationData && (
                                            <Lottie
                                                animationData={transferAnimationData}
                                                style={{ width: '150px', height: '150px', margin: '0 auto' }}
                                                loop={true}
                                            />
                                        )}
                                    </div>
                                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                                        <h6 className="fw-semibold text-primary-light mb-8">
                                            {isFetchingQR ? 'Generating QR Code...' : 'Connecting...'}
                                        </h6>
                                        <p className="text-secondary-light text-sm">
                                            {isFetchingQR 
                                                ? 'Please wait while we generate your QR code. This may take a few moments.'
                                                : 'Please wait while we connect your WhatsApp account. This may take a few moments.'
                                            }
                                        </p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {connectionStatus === 'disconnected' && qrCodeData ? (
                                        <>
                                            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                                                <div
                                                    style={{
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '8px',
                                                        padding: '16px',
                                                        backgroundColor: '#fff',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        margin: '0 auto',
                                                        maxWidth: '300px'
                                                    }}
                                                >
                                                    <QRCodeSVG 
                                                        value={qrCodeData}
                                                        size={256}
                                                        level="H"
                                                        includeMargin={true}
                                                    />
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                                                <h6 className="fw-semibold text-primary-light mb-8">Scan QR Code</h6>
                                                <p className="text-secondary-light text-sm">
                                                    Scan this QR code with WhatsApp to connect your account.
                                                </p>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                                                <Icon 
                                                    icon="mdi:whatsapp" 
                                                    width={80} 
                                                    height={80} 
                                                    style={{ color: '#25D366' }} 
                                                />
                                            </div>
                                            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                                                <h6 className="fw-semibold text-primary-light mb-8">WhatsApp Not Connected</h6>
                                                <p className="text-secondary-light text-sm">
                                                    Connect your WhatsApp account to start sending messages and notifications to your leads.
                                                </p>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Footer Section */}
                        <div
                            style={{
                                padding: '24px 32px',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '12px'
                            }}
                        >
                            <button
                                type="button"
                                className="btn btn-secondary-500 text-sm btn-sm px-24 py-12 radius-8"
                                onClick={closeQRModal}
                            >
                                Cancel
                            </button>
                            {isConnected ? (
                                <button
                                    type="button"
                                    className="btn btn-danger-500 text-sm btn-sm px-24 py-12 radius-8"
                                    onClick={handleDisconnect}
                                    disabled={isDisconnecting}
                                >
                                    {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                                </button>
                            ) : connectionStatus === 'connecting' && !qrCodeData ? (
                                <button
                                    type="button"
                                    className="btn btn-primary-500 text-sm btn-sm px-24 py-12 radius-8"
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                    onClick={handleReconnect}
                                    disabled={isReconnecting}
                                >
                                    {isReconnecting ? 'Reconnecting...' : 'Reconnect WhatsApp'}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-primary-500 text-sm btn-sm px-24 py-12 radius-8"
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                    onClick={handleReconnect}
                                    disabled={isReconnecting}
                                >
                                    {isReconnecting ? 'Reconnecting...' : 'Reconnect WhatsApp'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedNumber && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1050,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                    onClick={() => !isSubmitting && setShowDeleteModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            padding: '24px',
                            width: '90%',
                            maxWidth: '400px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h6 className="fw-semibold text-primary-light mb-12">Confirm Deletion</h6>
                        <p className="text-secondary-light text-sm mb-24">
                            Are you sure you want to delete WhatsApp number <strong>{selectedNumber.whatsappNumber}</strong>? This action cannot be undone.
                        </p>
                        <div className="d-flex justify-content-end gap-12">
                            <button
                                type="button"
                                className="btn btn-danger-500 text-sm btn-sm px-20 py-10 radius-8"
                                onClick={handleDeleteNumber}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Deleting...' : 'Yes, Delete'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary-500 text-sm btn-sm px-20 py-10 radius-8"
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setSelectedNumber(null);
                                }}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
};

export default WhatsappSettingsLayer;
